import React from 'react';
import LinkIcon from '../icons/LinkIcon';

interface CmsFile {
  url: string;
  title?: string;
}

interface ShoppingLinkItem {
  label: string;
  body: string;
  url: string;
  external?: boolean;
  image: CmsFile | null;
}

interface ShoppingLinkGroup {
  group_title: string;
  group_subtitle?: string;
  group_icon: CmsFile | null;
  items: ShoppingLinkItem[];
}

interface CmsShoppingLinksBlueData {
  name?: string;
  component?: string;
  body?: string;
  groups: ShoppingLinkGroup[];
}

interface CmsShoppingLinksBlueProps {
  data: CmsShoppingLinksBlueData;
  className?: string;
}

function parseBody(html: string): { description: string; linkText: string } {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const paragraphs = doc.querySelectorAll('p');

  const descriptions: string[] = [];
  let linkText = '';

  paragraphs.forEach((p) => {
    const anchor = p.querySelector('a');
    if (anchor) {
      linkText = anchor.textContent ?? '';
    } else {
      const text = p.textContent ?? '';
      if (text) descriptions.push(text);
    }
  });

  return { description: descriptions.join(' '), linkText };
}

export const CmsShoppingLinksBlue: React.FC<CmsShoppingLinksBlueProps> = ({
  data,
  className = '',
}) => {
  return (
    <section className={`font-montserrat py-16 px-500 ${className}`}>
      {/* Page Header */}
      {(data.name || data.body) && (
        <div className="mb-8">
          {data.name && (
            <h1 className="text-[64px] font-bold leading-[112.5%] tracking-[-1.92px] text-black/90">
              {data.name}
            </h1>
          )}
          {data.body && (
            <p className="mt-4 text-body-125 font-medium leading-[162.5%] text-gray-925 max-w-[601px]">
              {data.body}
            </p>
          )}
        </div>
      )}

      {/* Divider */}
      <hr className="border-gray-300 mt-300 mb-500" />

      {/* Card Grid */}
      {data.groups.map((group) => (
        <div key={group.group_title}>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-x-[23px] gap-y-350">
            {group.items.map((item) => {
              const { description, linkText } = parseBody(item.body);

              return (
                <div key={item.label} className="flex flex-col gap-150">
                  {/* Image Container */}
                  <div className="bg-[#F2F2F2] rounded-400 aspect-square flex items-center justify-center overflow-hidden">
                    {item.image && (
                      <img
                        src={item.image.url}
                        alt={item.label}
                        className="w-[250px] h-[250px] object-contain"
                      />
                    )}
                  </div>

                  {/* Card Content */}
                  <div className="flex flex-col gap-1">
                    <h3 className="text-[16px] font-bold leading-[150%] text-black/90">
                      {item.label}
                    </h3>
                    {description && (
                      <p className="text-[16px] font-medium leading-[162.5%] text-gray-700">
                        {description}
                      </p>
                    )}
                    {linkText && (
                      <a
                        href={item.url || undefined}
                        target={item.external ? '_blank' : '_self'}
                        rel={item.external ? 'noopener noreferrer' : undefined}
                        className="inline-flex items-center gap-1 text-[#0054A4] font-semibold text-sm mt-1 no-underline"
                      >
                        {linkText}
                        <LinkIcon
                          type={item.external ? 'ExternalIcon' : 'InternalIcon'}
                          className="w-4 h-4"
                        />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </section>
  );
};

export default CmsShoppingLinksBlue;
